/** @jsxImportSource @emotion/react */

import { useMutation, useQuery } from '@apollo/client'
import { useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { FIND_ONE_VIDEO_CATEGORY, UPDATE_VIDEO_CATEGORY } from 'queries/video'
import { toast } from 'utils/toast'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { PageLoader } from 'components/PageLoader'
import { findOneVideoCategory, findOneVideoCategoryVariables } from '__generatedTypes__/findOneVideoCategory'
import { updateVideoCategory, updateVideoCategoryVariables } from '__generatedTypes__/updateVideoCategory'
import * as commonClasses from '../commonStyles'
import ChapterCompletionForm, { ChapterCompletionData } from './ChapterCompletionForm'

const ChapterCompletionEdit = () => {
  const { videoCategoryId = '' } = useParams()
  const { navigate } = useLink()

  const [updateVideoCategory] = useMutation<updateVideoCategory, updateVideoCategoryVariables>(UPDATE_VIDEO_CATEGORY)
  const {
    data: videoCategoryData,
    loading,
    called,
  } = useQuery<findOneVideoCategory, findOneVideoCategoryVariables>(FIND_ONE_VIDEO_CATEGORY, {
    variables: { findOneVideoCategoryId: videoCategoryId },
    skip: !videoCategoryId,
    fetchPolicy: 'network-only',
  })
  const videoCategory = videoCategoryData?.findOneVideoCategory ?? null

  const redirectToVideoDashboard = () => {
    navigate(cmsRoutes.VIDEO_DASHBOARD, undefined, {
      searchParams: new URLSearchParams({
        subjectId: String(videoCategory?.subject?.id),
        categoryId: String(videoCategory?.id),
      }),
    })
  }

  const handleSubmit = useCallback(
    async (data: ChapterCompletionData) => {
      if (!videoCategory) {
        return
      }

      try {
        await updateVideoCategory({
          variables: {
            updateVideoCategoryInputFieldName: {
              id: videoCategory.id,
              chapterCompletionTitle: data.chapterCompletionTitle || null,
              chapterCompletionDescription: data.chapterCompletionDescription || null,
              chapterCompletionBtn: data.isNeedCompletedButton ? data.chapterCompletionBtn : null,
              chapterCompletionBtnUrl: data.isNeedCompletedButton ? data.chapterCompletionBtnUrl : null,
              chapterNotCompletionTitle: data.chapterNotCompletionTitle || null,
              chapterNotCompletionDescription: data.chapterNotCompletionDescription || null,
              chapterNotCompletionBtn: data.isNeedNotCompletedButton ? data.chapterNotCompletionBtn : null,
              chapterNotCompletionBtnUrl: data.isNeedNotCompletedButton ? data.chapterNotCompletionBtnUrl : null,
              chapterCompletionLive: data.chapterCompletionTitle ? undefined : false, // set isLive false on delete
            },
          },
        })

        redirectToVideoDashboard()
        toast.success('Chapter Completion updated successfully')
      } catch (error) {
        toast.error('Failed to update Chapter Completion')
      }
    },
    [videoCategory]
  )

  useEffect(() => {
    if (!videoCategory && called && !loading) {
      redirectToVideoDashboard()
    }
  }, [videoCategory, called, loading])

  if (loading || !videoCategory) {
    return <PageLoader />
  }

  return (
    <div css={commonClasses.containerPadding}>
      <ChapterCompletionForm videoCategory={videoCategory} handleSubmit={handleSubmit} />
    </div>
  )
}

export default ChapterCompletionEdit
